import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { CallHero } from "../components/CallHero";
import { CodeCompare } from "../components/CodeCompare";
import { DebugLog } from "../components/DebugLog";
import { DigitsLive } from "../components/DigitsLive";
import { EventLog } from "../components/EventLog";
import { NewCallDrawer } from "../components/NewCallDrawer";
import { TerminalFrame } from "../components/TerminalFrame";
import { consoleEmoji, consoleStatusText } from "../lib/hero";
import { mapConsoleKey, runControl } from "../lib/controls";
import { useControls } from "../lib/useControls";
import { useStore } from "../lib/store";

/**
 * Live operator console. Keyboard is an optional shortcut layer; buttons are the primary UX.
 */
export function Console({ active }: { active: boolean }) {
  const navigate = useNavigate();
  const { state, openNewCall, operator } = useStore();
  const { config } = useControls();
  const actions = state.snapshot.actions;
  const { sipText, sip, mode, party } = state.snapshot;
  const uuid = party.uuid ? party.uuid.slice(0, 8) : "--------";
  const phase = state.snapshot.state;
  const emoji = consoleEmoji(phase, state.snapshot.amd_label || party.amd);
  const statusText = consoleStatusText(state.snapshot);

  useEffect(() => {
    if (!active || !config.keyboardEnabled) return;
    const onKey = (event: KeyboardEvent) => {
      const action = mapConsoleKey(event, config);
      if (!action) return;
      event.preventDefault();
      runControl(action, {
        pathname: "/console",
        actions,
        navigate,
        openNewCall,
        operator,
      });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [actions, active, config, navigate, openNewCall, operator]);

  return (
    <div className="flex min-h-0 flex-1 flex-col p-3 md:p-4">
      <TerminalFrame
        title="operator"
        status={
          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
            {emoji ? (
              <span className="text-[13px] leading-none opacity-80" aria-hidden>
                {emoji}
              </span>
            ) : null}
            <span>{sipText || `SIP ${sip}`}</span>
            <span className="text-border-strong">·</span>
            <span>{mode}</span>
            <span className="text-border-strong">·</span>
            <span>{statusText}</span>
            <span className="text-border-strong">·</span>
            <span className="tabular-nums">{uuid}</span>
          </span>
        }
      >
        <CallHero />
        <div className="flex min-h-[96px] flex-col md:min-h-[110px] md:flex-row">
          <DigitsLive />
          <CodeCompare />
        </div>
        <ActionBar />
        <div className="mt-3 flex min-h-0 flex-1 flex-col md:flex-row">
          <EventLog />
          <DebugLog />
        </div>
      </TerminalFrame>
      <NewCallDrawer />
    </div>
  );
}
